/**
 * High-converting public landing page.
 *
 * Sections:
 *   1. Hero - bold headline + subhead + dual CTA (Subscribe / Free audit)
 *   2. Numbers / proof
 *   3. Features - rounded-corner cards
 *   4. How it works - 3 steps
 *   5. Pricing - monthly / yearly toggle
 *   6. FAQ accordion
 *   7. Footer
 *
 * Authenticated visitors are redirected to /dashboard (set in App.js).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Inbox,
  Layers,
  Sparkles,
  Send,
  BarChart3,
  ShieldCheck,
  GitBranch,
  ChevronDown,
  Check,
  Zap,
} from "lucide-react";
import Logo from "@/components/Logo";
import MarketingHeader from "@/components/MarketingHeader";
import Reveal from "@/components/Reveal";

const FEATURES = [
  {
    icon: Inbox,
    title: "Multi-inbox rotation",
    body: "Connect unlimited Gmail, Outlook, or any SMTP mailbox. Built-in Shuffle Bag load balancer keeps every inbox under its daily cap automatically.",
  },
  {
    icon: GitBranch,
    title: "A/B variants, native",
    body: "Test subject lines and bodies at the step level. Variants persist; the engine picks per-send. No external tooling.",
  },
  {
    icon: Sparkles,
    title: "AI Sequence Analyzer",
    body: "Powered by Claude Sonnet 4.5. Score subject, hook, body, CTA, spam risk, personalization. Get instant rewrites.",
  },
  {
    icon: ShieldCheck,
    title: "DNS health checker",
    body: "SPF, DKIM, DMARC scored at the click of a button. Fix deliverability problems before Gmail throttles you.",
  },
  {
    icon: Send,
    title: "Warmup engine",
    body: "Automatic inbox-to-inbox warmup with a 4-week ramp. Builds reputation while you focus on copy.",
  },
  {
    icon: BarChart3,
    title: "Real analytics",
    body: "Live opens, clicks, replies, bounces with per-step funnels. Stop guessing why nobody answers.",
  },
];

const HOW_IT_WORKS = [
  { n: "01", title: "Connect", body: "Drop in a Gmail App Password or OAuth your Workspace mailbox. 30 seconds." },
  { n: "02", title: "Compose", body: "Build a multi-step sequence with variables, A/B variants, and AI rewrites." },
  { n: "03", title: "Ship", body: "Upload your lead CSV. We rotate inboxes, jitter timing, and track everything." },
];

const FAQ = [
  {
    q: "How is this different from Instantly or Lemlist?",
    a: "Same core engine - multi-inbox rotation, sequences, warmup, AI rewrites - at a fraction of the price, with no per-seat fees and an admin you actually control. Built for bootstrapped founders, not enterprise procurement.",
  },
  {
    q: "Do I need my own domain & DNS setup?",
    a: "You'll send from inboxes you own. We don't host your sending domain - you connect Gmail, Outlook, or any SMTP host. We do run a DNS health check on SPF / DKIM / DMARC so you know your domain is configured right.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Subscription is month-to-month or yearly. Cancel inside the billing portal and you keep access until the end of the current period.",
  },
  {
    q: "Will my emails land in spam?",
    a: "We can't promise inbox placement (nobody can - that's on your copy, sender reputation, and DNS). What we DO is: rotate inboxes within daily caps, jitter send timing, inject open pixels + click wrappers tastefully, auto-detect unsubscribe language, and warm up your mailboxes inbox-to-inbox.",
  },
  {
    q: "What happens if my payment fails?",
    a: "You get a 48-hour grace period to update your card - no service interruption. After that, the dashboard locks until you re-subscribe. We do not run automatic background jobs to check this - Stripe webhooks drive every state change.",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState("yearly"); // 'monthly' | 'yearly'
  const yearly = billing === "yearly";

  const monthlyPrice = useMemo(() => (yearly ? 20 : 25), [yearly]);
  const totalAnnual = yearly ? 240 : 25 * 12;
  const subscribe = () => navigate(`/register?plan=${billing}`);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* NAV */}
      <MarketingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" aria-hidden />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.06), transparent 60%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 pt-24 pb-24 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#27272a] bg-[#0d0d0d] text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-400">
              <Sparkles className="w-3 h-3" strokeWidth={1.8} />
              Cold email · made boring · finally
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] mt-7">
              Cold email that<br />
              <span className="text-neutral-500">ships replies.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-neutral-400 mt-7 text-lg leading-relaxed max-w-2xl mx-auto">
              Multi-inbox rotation, AI-scored copy, warmup engine, real analytics -
              in one tool you fully control. Built for founders who refuse to pay
              $300/mo for a glorified send button.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={subscribe}
                data-testid="hero-subscribe"
                className="tactile inline-flex items-center gap-2 bg-white text-black px-7 py-4 rounded-md font-medium text-sm hover:bg-neutral-200"
              >
                Start sending
                <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </button>
              <a
                href="/free-audit"
                data-testid="hero-audit"
                className="tactile inline-flex items-center gap-2 border border-[#27272a] bg-[#0d0d0d] text-white px-7 py-4 rounded-md font-medium text-sm hover:bg-[#161616]"
              >
                Try the free AI audit
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* NUMBERS / PROOF */}
      <section className="border-y border-[#161616]">
        <Reveal>
          <div className="max-w-5xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { v: "∞", l: "Inboxes per account" },
              { v: "50k+", l: "Sends per month" },
              { v: "6", l: "Score dimensions" },
              { v: "Sonnet 4.5", l: "AI under the hood" },
            ].map(({ v, l }) => (
              <div key={l}>
                <div className="font-display text-4xl font-semibold">{v}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-mono mt-1">
                  {l}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* FEATURES */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <Reveal>
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-mono">
            What's inside
          </div>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 max-w-2xl">
            Everything you need.
            <br />
            <span className="text-neutral-500">Nothing you don't.</span>
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={Math.min(i * 60, 240)}>
              <div
                className="tactile border border-[#1a1a1a] bg-[#0d0d0d] rounded-2xl p-6 hover:bg-[#111] hover:-translate-y-[2px] hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)] h-full"
                data-testid={`feature-${f.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="w-9 h-9 rounded-lg border border-[#27272a] bg-[#0a0a0a] flex items-center justify-center">
                  <f.icon className="w-4 h-4" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-lg font-medium mt-4">{f.title}</h3>
                <p className="text-neutral-400 text-sm mt-2 leading-relaxed">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-6 py-24 border-t border-[#161616]">
        <Reveal>
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-mono">
            How it works
          </div>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3">
            Three steps. Then send.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-3 mt-10">
          {HOW_IT_WORKS.map((s, i) => (
            <Reveal key={s.n} delay={i * 80}>
              <div className="tactile border border-[#1a1a1a] bg-[#0d0d0d] rounded-2xl p-6 hover:bg-[#111] hover:-translate-y-[2px] h-full">
                <div className="font-mono text-xs text-neutral-500">{s.n}</div>
                <h3 className="font-display text-2xl font-semibold mt-3">{s.title}</h3>
                <p className="text-neutral-400 text-sm mt-3 leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section
        className="max-w-3xl mx-auto px-6 py-24 border-t border-[#161616]"
        id="pricing"
      >
        <Reveal>
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-mono text-center">
            Pricing
          </div>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 text-center">
            One plan. Everything in.
          </h2>
        </Reveal>
        <Reveal delay={160}>
          <div className="flex justify-center mt-7">
            <div className="inline-flex p-1 rounded-md border border-[#27272a] bg-[#0d0d0d] text-xs font-mono">
              <button
                onClick={() => setBilling("monthly")}
                data-testid="billing-toggle-monthly"
                className={`tactile px-4 py-1.5 rounded uppercase tracking-[0.18em] ${
                  !yearly ? "bg-white text-black" : "text-neutral-400"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling("yearly")}
                data-testid="billing-toggle-yearly"
                className={`tactile px-4 py-1.5 rounded uppercase tracking-[0.18em] ${
                  yearly ? "bg-white text-black" : "text-neutral-400"
                }`}
              >
                Yearly · save 20%
              </button>
            </div>
          </div>
        </Reveal>
        <Reveal delay={220}>
          <div
            className="mt-8 border border-[#27272a] rounded-2xl overflow-hidden"
            style={{ background: "linear-gradient(135deg,#0d0d0d 0%,#161616 100%)" }}
          >
            <div className="p-10">
              <div className="flex items-baseline gap-2 justify-center">
                <div className="font-display text-6xl font-semibold">
                  €{monthlyPrice}
                </div>
                <div className="text-neutral-500 text-sm font-mono">/month</div>
              </div>
              {yearly && (
                <div className="text-center text-xs font-mono text-neutral-500 mt-1">
                  billed €{totalAnnual} annually
                </div>
              )}
              <ul className="mt-8 space-y-2 text-sm text-neutral-300 max-w-md mx-auto">
                {[
                  "Unlimited inboxes (Gmail / Outlook / SMTP)",
                  "Unlimited campaigns & leads",
                  "AI Sequence Analyzer (Claude Sonnet 4.5)",
                  "Multi-inbox Shuffle Bag rotation",
                  "Warmup engine + DNS health checker",
                  "A/B variants & live analytics",
                  "Full PDF audit exports",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" strokeWidth={1.8} />
                    {b}
                  </li>
                ))}
              </ul>
              <button
                onClick={subscribe}
                data-testid="pricing-subscribe"
                className="tactile mt-9 w-full flex items-center justify-center gap-2 bg-white text-black px-7 py-3.5 rounded-md font-medium text-sm hover:bg-neutral-200"
              >
                Subscribe to {billing} plan
                <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-24 border-t border-[#161616]">
        <Reveal>
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-mono text-center">
            FAQ
          </div>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 text-center">
            Answers.
          </h2>
        </Reveal>
        <div className="mt-10 space-y-2">
          {FAQ.map((item, i) => (
            <Reveal key={item.q} delay={Math.min(i * 50, 200)}>
              <FaqItem q={item.q} a={item.a} testId={`faq-${i + 1}`} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#161616] mt-6">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <Logo variant="white" className="h-5 opacity-70" />
          <div className="flex flex-wrap items-center gap-5 text-xs font-mono uppercase tracking-[0.2em] text-neutral-600">
            <span className="text-neutral-600">OutreachOS - cold email, finally boring</span>
            <a href="/terms" className="hover:text-white" data-testid="footer-terms">
              Terms
            </a>
            <a href="/privacy" className="hover:text-white" data-testid="footer-privacy">
              Privacy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FaqItem({ q, a, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen((o) => !o)}
      data-testid={testId}
      className="tactile block w-full text-left border border-[#1a1a1a] bg-[#0d0d0d] rounded-2xl p-5 hover:bg-[#111]"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="font-display text-base sm:text-lg">{q}</div>
        <ChevronDown
          className={`w-4 h-4 text-neutral-500 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
          strokeWidth={1.8}
        />
      </div>
      {open && (
        <div className="text-sm text-neutral-400 mt-3 leading-relaxed">{a}</div>
      )}
    </button>
  );
}
